import React from "react";
import * as urls from "../../InSilicoPredictionsUrls";
import * as constants from "../../InSilicoPredictionsConstants";
import VersionText from "../../../../../../components/atoms/VersionText";
import { getIndicativePrediction } from "./getIndicativePrediction";
import { getThresholds } from "./getThresholds";
import { getScores, getScoresArray } from "./getScores";
import { setCalculatedInformationPerDb } from "./setCalculatedInformationPerDb";

const getDbnsfpVersion = (version) => {
  if (!version) return null;
  return (
    <VersionText
      versionText={version}
      anchorUrl={urls.dbnsfpUrl}
      anchorText="dbNSFP"
    />
  );
};

const getVersion = (version) => {
  if (!version) return null;
  return <VersionText versionText={version} />;
};

export const getInsilicoDbList = (data, thresholds) => {
  if (!thresholds || !data) return [];
  const options = [
    {
      name: "SIFT",
      url: urls.siftUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        rankscore: "converted rankscore",
      },
      thresholds: getThresholds("sift", thresholds),
      scores: getScores(data, "sift_score"),
      scoresArray: getScoresArray(data, "sift_score"),
      rankScores: getScores(data, "sift_converted_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "sift_pred",
        constants.PREDICTIONS_GROUP_1
      ),
    },
    {
      name: "SIFT4G",
      url: urls.sift4gUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        rankscore: "converted rankscore",
      },
      thresholds: getThresholds("sift4g", thresholds),
      scores: getScores(data, "sift4g_score"),
      scoresArray: getScoresArray(data, "sift4g_score"),
      rankScores: getScores(data, "sift4g_converted_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "sift4g_pred",
        constants.PREDICTIONS_GROUP_1
      ),
    },
    {
      name: "PROVEAN",
      url: urls.proveanUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        rankscore: "converted rankscore",
      },
      thresholds: getThresholds("provean", thresholds),
      scores: getScores(data, "provean_score"),
      scoresArray: getScoresArray(data, "provean_score"),
      rankScores: getScores(data, "provean_converted_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "provean_pred",
        constants.PREDICTIONS_GROUP_2
      ),
    },
    {
      name: "MVP",
      url: urls.mvpUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("mvp", thresholds),
      scores: getScores(data, "mvp_score"),
      scoresArray: getScoresArray(data, "mvp_score"),
      rankScores: getScores(data, "mvp_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "mvp_pred",
        constants.PREDICTIONS_GROUP_1
      ),
    },
    {
      name: "MutPred",
      url: urls.mutpredUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("mutpred", thresholds),
      scores: getScores(data, "mutpred_score"),
      scoresArray: getScoresArray(data, "mutpred_score"),
      rankScores: getScores(data, "mutpred_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "mutPred_pred",
        constants.PREDICTIONS_GROUP_1
      ),
    },
    {
      name: "PrimateAI",
      url: urls.primateaiUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("primateai", thresholds),
      scores: getScores(data, "primateai_score"),
      scoresArray: getScoresArray(data, "primateai_score"),
      rankScores: getScores(data, "primateai_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "primateai_pred",
        constants.PREDICTIONS_GROUP_1
      ),
    },
    {
      name: "BayesDel noAF",
      url: urls.bayesdelUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        score: "noAF score",
        rankscore: "noAF rankscore",
      },
      thresholds: getThresholds("bayesdel_noaf", thresholds),
      scores: getScores(data, "bayesdel_noaf_score"),
      scoresArray: getScoresArray(data, "bayesdel_noaf_score"),
      rankScores: getScores(data, "bayesdel_noaf_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "bayesdel_noaf_pred",
        constants.PREDICTIONS_GROUP_1
      ),
      isMeta: true,
    },
    {
      name: "BayesDel addAF",
      url: urls.bayesdelUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        score: "addAF score",
        rankscore: "addAF rankscore",
      },
      thresholds: getThresholds("bayesdel_addaf", thresholds),
      scores: getScores(data, "bayesdel_addaf_score"),
      scoresArray: getScoresArray(data, "bayesdel_addaf_score"),
      rankScores: getScores(data, "bayesdel_addaf_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "bayesdel_addaf_pred",
        constants.PREDICTIONS_GROUP_1
      ),
      isMeta: true,
    },
    {
      name: "LIST-S2",
      url: urls.lists2Url,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("list_s2", thresholds),
      scores: getScores(data, "list_s2_score"),
      scoresArray: getScoresArray(data, "list_s2_score"),
      rankScores: getScores(data, "list_s2_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "list_s2_pred",
        constants.PREDICTIONS_GROUP_1
      ),
    },
    {
      name: "MutationTaster",
      url: urls.MutationTasterUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        rankscore: "converted rankscore",
      },
      thresholds: getThresholds("mutationtaster", thresholds),
      scores: getScores(data, "mutationtaster_score"),
      scoresArray: getScoresArray(data, "mutationtaster_score"),
      rankScores: getScores(data, "mutationtaster_converted_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "mutationtaster_pred",
        constants.PREDICTIONS_GROUP_3
      ),
    },
    {
      name: "Mutation assessor",
      url: urls.MutationAssessorUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("mutationassessor", thresholds),
      scores: getScores(data, "mutationassessor_score"),
      scoresArray: getScoresArray(data, "mutationassessor_score"),
      rankScores: getScores(data, "mutationassessor_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "mutationassessor_pred",
        constants.PREDICTIONS_GROUP_6
      ),
    },
    {
      name: "FATHMM",
      url: urls.fathmmUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        rankscore: "converted rankscore",
      },
      thresholds: getThresholds("fathmm", thresholds),
      scores: getScores(data, "fathmm_score"),
      scoresArray: getScoresArray(data, "fathmm_score"),
      rankScores: getScores(data, "fathmm_converted_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "fathmm_pred",
        constants.PREDICTIONS_GROUP_1
      ),
    },
    {
      name: "FATHMM-MKL",
      url: urls.fathmmMklUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        score: "coding score",
        rankscore: "coding rankscore",
      },
      thresholds: getThresholds("fathmm_mkl", thresholds),
      scores: getScores(data, "fathmm_mkl_coding_score"),
      scoresArray: getScoresArray(data, "fathmm_mkl_coding_score"),
      rankScores: getScores(data, "fathmm_mkl_coding_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "fathmm_mkl_coding_pred",
        constants.PREDICTIONS_GROUP_2
      ),
    },
    {
      name: "FATHMM-XF",
      url: urls.fathmmXfUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        score: "coding score",
        rankscore: "coding rankscore",
      },
      thresholds: getThresholds("fathmm_xf", thresholds),
      scores: getScores(data, "fathmm_xf_coding_score"),
      scoresArray: getScoresArray(data, "fathmm_xf_coding_score"),
      rankScores: getScores(data, "fathmm_xf_coding_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "fathmm_xf_coding_pred",
        constants.PREDICTIONS_GROUP_2
      ),
    },
    {
      name: "LRT",
      url: urls.LRTUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        rankscore: "converted rankscore",
      },
      thresholds: getThresholds("lrt", thresholds),
      scores: getScores(data, "lrt_score"),
      scoresArray: getScoresArray(data, "lrt_score"),
      rankScores: getScores(data, "lrt_converted_rankscore"),
      omegaScores: getScores(data, "lrt_omega"),
      indicativePrediction: getIndicativePrediction(
        data,
        "lrt_pred",
        constants.PREDICTIONS_GROUP_4
      ),
    },
    {
      name: "DEOGEN2",
      url: urls.deogen2Url,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("deogen2", thresholds),
      scores: getScores(data, "deogen2_score"),
      scoresArray: getScoresArray(data, "deogen2_score"),
      rankScores: getScores(data, "deogen2_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "deogen2_pred",
        constants.PREDICTIONS_GROUP_1
      ),
    },
    {
      name: "M-CAP",
      url: urls.mcapUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("m_cap", thresholds),
      scores: getScores(data, "m_cap_score"),
      scoresArray: getScoresArray(data, "m_cap_score"),
      rankScores: getScores(data, "m_cap_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "m_cap_pred",
        constants.PREDICTIONS_GROUP_1
      ),
    },
    {
      name: "CADD",
      url: urls.caddUrl,
      version: getVersion(data?.caddVersion),
      thresholds: getThresholds("cadd", thresholds),
      scores: getScores(data, "cadd_score"),
      scoresArray: getScoresArray(data, "cadd_score"),
      isPremium: true,
      customSubText: (
        <span className="tw-text-3xs tw-inline-block tw-italic">
          Since version 1.6, CADD integrates several splice prediction scores,
          see{" "}
          <a href={urls.caddSplicingURL} target="_blank">
            publication
          </a>
        </span>
      ),
    },
    {
      name: "DANN",
      version: getVersion(data.dannVersion),
      url: urls.dannUrl,
      thresholds: getThresholds("dann", thresholds),
      scores: getScores(data, "dann_score"),
      scoresArray: getScoresArray(data, "dann_score"),
    },
    {
      name: "MetaLR",
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("metalr", thresholds),
      scores: getScores(data, "metalr_score"),
      scoresArray: getScoresArray(data, "metalr_score"),
      rankScores: getScores(data, "metalr_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "metalr_pred",
        constants.PREDICTIONS_GROUP_1
      ),
      isMeta: true,
    },
    {
      name: "MetaSVM",
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("metasvm", thresholds),
      scores: getScores(data, "metasvm_score"),
      scoresArray: getScoresArray(data, "metasvm_score"),
      rankScores: getScores(data, "metasvm_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "metasvm_pred",
        constants.PREDICTIONS_GROUP_1
      ),
      isMeta: true,
    },
    {
      name: "MetaRNN",
      url: urls.metaRnnUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("metarnn", thresholds),
      scores: getScores(data, "metarnn_score"),
      scoresArray: getScoresArray(data, "metarnn_score"),
      rankScores: getScores(data, "metarnn_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "metarnn_pred",
        constants.PREDICTIONS_GROUP_1
      ),
      isMeta: true,
    },
    {
      name: "REVEL",
      url: urls.revelUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("revel", thresholds),
      scores: getScores(data, "revel_score"),
      scoresArray: getScoresArray(data, "revel_score"),
      rankScores: getScores(data, "revel_rankscore"),
      indicativePrediction: getIndicativePrediction(
        data,
        "revel_pred",
        constants.PREDICTIONS_GROUP_1
      ),
      isMeta: true,
    },
    {
      name: "ALoFT",
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      thresholds: getThresholds("aloft", thresholds),
      scores: getScores(data, "aloft_score"),
      scoresArray: getScoresArray(data, "aloft_score"),
      indicativePrediction: getIndicativePrediction(data, "aloft"),
    },
    {
      name: "EIGEN",
      url: urls.eigenUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        score: "raw coding",
        rankscore: "raw coding rankscore",
      },
      thresholds: getThresholds("eigen", thresholds),
      scores: getScores(data, "eigen_raw_coding"),
      scoresArray: getScoresArray(data, "eigen_raw_coding"),
      rankScores: getScores(data, "eigen_raw_coding_rankscore"),
      indicativePrediction: getIndicativePrediction(data, "eigen_pred_coding"),
    },
    {
      name: "EIGEN PC",
      url: urls.eigenUrl,
      version: getDbnsfpVersion(data?.dbnsfpVersion),
      customKeys: {
        score: "PC raw coding score",
        rankscore: "PC raw coding rankscore",
      },
      thresholds: getThresholds("eigen_pc", thresholds),
      scores: getScores(data, "eigen_pc_raw_coding"),
      scoresArray: getScoresArray(data, "eigen_pc_raw_coding"),
      rankScores: getScores(data, "eigen_pc_raw_coding_rankscore"),
      phredCodingScores: getScores(data, "eigen_pc_phred_coding"),
    },
    {
      name: "dbscSNV",
      url: urls.dbscSNVUrl,
      version: getVersion(data?.dbnsfpDbscsnvVersion),
      customKeys: {
        score: "ADA score",
        rankscore: "RF rankscore",
      },
      thresholds: getThresholds("scsnv_ada", thresholds),
      scores: getScores(data, "ada_score"),
      scoresArray: getScoresArray(data, "ada_score"),
      rankScores: getScores(data, "rf_score"),
    },
    {
      name: "EVE",
      url: urls.eveUrl,
      version: getVersion(data?.eve_version),
      thresholds: getThresholds("eve", thresholds),
      scores: getScores(data, "eve_score"),
      scoresArray: getScoresArray(data, "eve_score"),
      indicativePrediction: getIndicativePrediction(
        data,
        "eve_pathogenicities"
      ),
    },
    {
      name: "AlphaMissense",
      url: urls.alphaMissenseUrl,
      version: getVersion(data?.alpha_missense_version),
      thresholds: getThresholds("alphamissense", thresholds),
      scores: getScores(data, "alpha_missense_score"),
      scoresArray: getScoresArray(data, "alpha_missense_score"),
      indicativePrediction: getIndicativePrediction(
        data,
        "alpha_missense_pred"
      ),
    },
    {
      name: "BLOSUM",
      url: urls.blosumUrl,
      version: getVersion("BLOSUM100"),
      thresholds: getThresholds("blosum", thresholds),
      scores: getScores(data, "blosum"),
      scoresArray: getScoresArray(data, "blosum"),
    },
    {
      url: "https://www.mitomap.org/foswiki/bin/view/MITOMAP/WebHome",
      name: "MitoTip",
      thresholds: getThresholds("mitotip", thresholds),
      scores: getScores(data, "mitotip_score"),
      scoresArray: getScoresArray(data, "mitotip_score"),
    },
    {
      name: "MitImpact",
      url: "https://mitimpact.css-mendel.it/",
      version: getVersion("3.0.8"),
      thresholds: getThresholds("mitimpact", thresholds),
      scores: getScores(data, "apogee_score"),
      scoresArray: getScoresArray(data, "apogee_score"),
      indicativePrediction: getIndicativePrediction(
        data,
        "apogee",
        constants.PREDICTION_GROUP_MIT_IMPACT
      ),
    },
    {
      name: "MaxEntScan",
      version: getVersion(data?.maxentscanVersion),
      url: "http://hollywood.mit.edu/burgelab/maxent/Xmaxentscan_scoreseq.html",
      scores: getScores(data, "maxentscanScores"),
      scoresArray: getScoresArray(data, "maxentscanScores"),
      thresholds: getThresholds("maxentscan", thresholds),
    },
  ];
  return setCalculatedInformationPerDb(options);
};
